import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { DataGrid } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import './ResizableDataGrid.css';

export const ResizableDataGrid = ({ columns: initialColumns, onColumnResize, ...props }) => {
  const [columns, setColumns] = useState(() =>
    initialColumns.map(col => ({
      ...col,
      width: col.width || (col.flex ? 150 : col.minWidth || 100)
    }))
  );

  // Re-sync when the caller passes new column defs (e.g. renderCell/valueFormatter
  // closures changing due to external state like the selected timezone) while
  // preserving any width the user manually set via drag-resize.
  useEffect(() => {
    setColumns(prevColumns => {
      const prevWidthByField = new Map(prevColumns.map(col => [col.field, col.width]));
      return initialColumns.map(col => ({
        ...col,
        width: prevWidthByField.get(col.field) || col.width || (col.flex ? 150 : col.minWidth || 100)
      }));
    });
  }, [initialColumns]);

  const [resizing, setResizing] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Direct resize function for immediate response (no RAF to avoid jerkiness)
  const updateColumnWidth = useCallback((newWidth, columnField) => {
    const column = columns.find(col => col.field === columnField);
    const minWidth = column?.minWidth || 80;
    const constrainedWidth = Math.max(minWidth, newWidth);
    
    setColumns(prev => prev.map(col => 
      col.field === columnField 
        ? { ...col, width: constrainedWidth, flex: undefined }
        : col
    ));
  }, [columns]);

  const handleMouseDown = useCallback((e) => {
    const headerElement = e.target.closest('.MuiDataGrid-columnHeader');
    if (!headerElement) return;
    
    const columnField = headerElement.getAttribute('data-field');
    const column = columns.find(col => col.field === columnField);
    
    if (!column || column.resizable === false) return;

    e.preventDefault();
    e.stopPropagation();
    
    setResizing(columnField);
    startXRef.current = e.clientX;
    startWidthRef.current = column.width || 150;
    setDragOffset(0);
    
    // Enhanced visual feedback
    document.body.classList.add('resizing-mode');
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'none';
    
    // Add visual indicators
    headerElement.classList.add('resize-active', 'resizing');
  }, [columns]);

  const handleMouseMove = useCallback((e) => {
    if (!resizing) return;
    
    const column = columns.find(col => col.field === resizing);
    const minWidth = column?.minWidth || 80;
    const diff = e.clientX - startXRef.current;
    const newWidth = Math.max(minWidth, startWidthRef.current + diff);
    
    // Add visual indicator when at minimum width
    const headerElement = document.querySelector(`[data-field="${resizing}"]`);
    if (headerElement) {
      if (newWidth === minWidth) {
        headerElement.classList.add('at-min-width');
      } else {
        headerElement.classList.remove('at-min-width');
      }
    }
    
    // Update immediately for smooth resizing
    updateColumnWidth(newWidth, resizing);
  }, [resizing, updateColumnWidth, columns]);

  const handleMouseUp = useCallback(() => {
    if (resizing) {
      const column = columns.find(col => col.field === resizing);
      if (onColumnResize && column) {
        onColumnResize(resizing, column.width);
      }
      
      // Remove visual indicators
      const headerElement = document.querySelector(`[data-field="${resizing}"]`);
      if (headerElement) {
        headerElement.classList.remove('resize-active', 'resizing', 'at-min-width');
      }
    }
    
    setResizing(null);
    setDragOffset(0);
    startXRef.current = 0;
    startWidthRef.current = 0;
    
    // Clean up enhanced visual feedback
    document.body.classList.remove('resizing-mode');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.body.style.pointerEvents = '';
  }, [resizing, columns, onColumnResize]);

  useEffect(() => {
    if (resizing) {
      const handleMove = (e) => handleMouseMove(e);
      const handleUp = () => handleMouseUp();
      
      // Use passive: false for better performance during resize
      document.addEventListener('mousemove', handleMove, { passive: false });
      document.addEventListener('mouseup', handleUp, { once: true });
      
      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleUp);
      };
    }
  }, [resizing, handleMouseMove, handleMouseUp]);

  // Optimized resize handles creation
  const addResizeHandles = useCallback(() => {
    const headers = document.querySelectorAll('.MuiDataGrid-columnHeader:not(.resize-enhanced)');
    
    headers.forEach(header => {
      const columnField = header.getAttribute('data-field');
      const column = columns.find(col => col.field === columnField);
      
      if (!column || column.resizable === false) return;
      
      header.classList.add('resize-enhanced');
      
      // Create resize handle with better performance
      const resizeHandle = document.createElement('div');
      resizeHandle.className = 'column-resize-handle';
      resizeHandle.style.cssText = `
        position: absolute;
        right: -5px;
        top: 0;
        height: 100%;
        width: 10px;
        cursor: col-resize;
        background: transparent;
        z-index: 1000;
        transition: all 0.15s ease;
        border-radius: 2px;
      `;
      
      // Add hover effects
      resizeHandle.addEventListener('mouseenter', () => {
        if (!resizing) {
          resizeHandle.style.background = 'rgba(25, 118, 210, 0.12)';
          resizeHandle.style.width = '12px';
          resizeHandle.style.right = '-6px';
        }
      });
      
      resizeHandle.addEventListener('mouseleave', () => {
        if (!resizing) {
          resizeHandle.style.background = 'transparent';
          resizeHandle.style.width = '10px';
          resizeHandle.style.right = '-5px';
        }
      });
      
      resizeHandle.addEventListener('mousedown', handleMouseDown);
      
      header.style.position = 'relative';
      header.appendChild(resizeHandle);
    });
  }, [columns, handleMouseDown, resizing]);

  // Use intersection observer for better performance
  useEffect(() => {
    const timer = setTimeout(addResizeHandles, 50);
    
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldUpdate = true;
        }
      });
      
      if (shouldUpdate) {
        setTimeout(addResizeHandles, 10);
      }
    });
    
    const gridElement = document.querySelector('.MuiDataGrid-root');
    if (gridElement) {
      observer.observe(gridElement, { 
        childList: true, 
        subtree: true,
        attributes: false,
        characterData: false
      });
    }
    
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [addResizeHandles]);

  // Memoize the enhanced styles for better performance
  const enhancedStyles = useMemo(() => ({
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
      position: 'relative'
    },
    '& .MuiDataGrid-columnHeader': {
      borderRight: 'none',
      borderLeft: 'none',
      position: 'relative',
      overflow: 'visible !important',
      transition: 'none !important', // Disable all transitions during resize
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)'
      },
      '&:not(.resizing)': {
        transition: 'width 0.08s ease-out !important' // Only when not resizing
      }
    },
    '& .MuiDataGrid-cell': {
      borderRight: 'none',
      borderLeft: 'none'
    },
    '& .MuiDataGrid-row': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)'
      }
    },
    '& .column-resize-handle': {
      opacity: resizing ? 1 : 0.7
    },
    ...props.sx
  }), [resizing, props.sx]);

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <DataGrid
        {...props}
        columns={columns}
        disableColumnResize={true}
        sx={enhancedStyles}
      />
    </Box>
  );
};
